/**
 * Recognising a chat turn that failed because the AI service is out of quota.
 *
 * Two shapes reach the chat panel through the widget's `onTurnError`:
 *
 * - adk-be-app's typed event, once it has retried on its own: `errorCode: 'RATE_LIMITED'` with
 *   `customMetadata: { type: 'rate_limited', retryAfterSeconds }` (adk-be-app src/agent/rate_limit.py);
 * - the raw provider error from a backend without that, e.g. a bare
 *   `litellm.MidStreamFallbackError … Vertex_ai_betaException … 429 … TPM quota exhausted` line, or an HTTP 429.
 *
 * Either way the panel shows one friendly message with a countdown instead of the exception text.
 */

const RATE_LIMIT_TEXT = /RateLimitError|rate.?limit|\b429\b|quota|RESOURCE_EXHAUSTED|too many requests|\bTPM\b/i;
const RETRY_IN_TEXT = /retry(?:[- ]after)?(?: in)?[^0-9]{0,12}(\d+(?:\.\d+)?)\s*(ms|milliseconds?|s|sec|seconds?)\b/i;

/** Seconds to wait when nothing says how long. */
export const DEFAULT_RETRY_SECONDS = 15;
const MAX_RETRY_SECONDS = 120;

const clampSeconds = (value) => Math.min(MAX_RETRY_SECONDS, Math.max(1, Math.round(value)));

const secondsFromText = (text) => {
    const match = RETRY_IN_TEXT.exec(text || '');
    if (!match) { return null; }
    const amount = Number(match[1]);
    return match[2].toLowerCase().startsWith('m') ? amount / 1000 : amount;
};

/**
 * `{ retryAfterSeconds, details }` when the failed turn described by *info* (the widget's `TurnErrorInfo`) was rate
 * limited, `null` otherwise. `details` is the raw text, for a folded "Details".
 */
export function rateLimitFromTurnError(info) {
    if (!info) { return null; }
    const metadata = info.customMetadata || {};
    const typed = info.errorCode === 'RATE_LIMITED' || metadata.type === 'rate_limited';
    const message = typeof info.message === 'string' ? info.message : '';
    if (!typed && info.status !== 429 && !RATE_LIMIT_TEXT.test(message)) { return null; }
    const said = Number(metadata.retryAfterSeconds);
    const seconds = Number.isFinite(said) && said > 0 ? said : secondsFromText(message) ?? DEFAULT_RETRY_SECONDS;
    return { details: message, retryAfterSeconds: clampSeconds(seconds) };
}
